import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Badge } from './ui/badge';
import { Tabs, TabsList, TabsTrigger } from './ui/tabs';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogClose
} from './ui/dialog';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger
} from './ui/dropdown-menu';
import {
  Tooltip, TooltipContent, TooltipProvider, TooltipTrigger
} from './ui/tooltip';
import { toast } from 'sonner';
import QRCodeGenerator from './QRCodeGenerator';
import {
  Share2, Twitter, Facebook, Linkedin, Mail, Link2, MessageSquare, Copy, Send,
  MoreHorizontal, ExternalLink, RefreshCw, Search, TrendingUp, SortAsc,
  Shield, ShieldAlert, ShieldCheck, AlertTriangle, QrCode, Download
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Reddit icon component
const RedditIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M12 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0zm5.01 4.744c.688 0 1.25.561 1.25 1.249a1.25 1.25 0 0 1-2.498.056l-2.597-.547-.8 3.747c1.824.07 3.48.632 4.674 1.488.308-.309.73-.491 1.207-.491.968 0 1.754.786 1.754 1.754 0 .716-.435 1.333-1.01 1.614a3.111 3.111 0 0 1 .042.52c0 2.694-3.13 4.87-7.004 4.87-3.874 0-7.004-2.176-7.004-4.87 0-.183.015-.366.043-.534A1.748 1.748 0 0 1 4.028 12c0-.968.786-1.754 1.754-1.754.463 0 .898.196 1.207.49 1.207-.883 2.878-1.43 4.744-1.487l.885-4.182a.342.342 0 0 1 .14-.197.35.35 0 0 1 .238-.042l2.906.617a1.214 1.214 0 0 1 1.108-.701zM9.25 12C8.561 12 8 12.562 8 13.25c0 .687.561 1.248 1.25 1.248.687 0 1.248-.561 1.248-1.249 0-.688-.561-1.249-1.249-1.249zm5.5 0c-.687 0-1.248.561-1.248 1.25 0 .687.561 1.248 1.249 1.248.688 0 1.249-.561 1.249-1.249 0-.687-.562-1.249-1.25-1.249zm-5.466 3.99a.327.327 0 0 0-.231.094.33.33 0 0 0 0 .463c.842.842 2.484.913 2.961.913.477 0 2.105-.056 2.961-.913a.361.361 0 0 0 .029-.463.33.33 0 0 0-.464 0c-.547.533-1.684.73-2.512.73-.828 0-1.979-.196-2.512-.73a.326.326 0 0 0-.232-.095z"/>
  </svg>
);

// QR Code inline component for dialogs
const QRCodeInline = ({ url, title, source }) => {
  const [qrDataUrl, setQrDataUrl] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  
  const getTrackedUrl = () => {
    let baseUrl = url;
    if (!baseUrl.startsWith('http')) {
      baseUrl = `${window.location.origin}${url}`;
    }
    const utmParams = new URLSearchParams({
      utm_source: 'qr_code',
      utm_medium: 'offline',
      utm_campaign: source || 'share'
    });
    const separator = baseUrl.includes('?') ? '&' : '?';
    return `${baseUrl}${separator}${utmParams.toString()}`;
  };

  React.useEffect(() => {
    const generateQR = async () => {
      const trackedUrl = getTrackedUrl();
      try {
        const qrApiUrl = `https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(trackedUrl)}&format=png&margin=10`;
        const response = await fetch(qrApiUrl);
        const blob = await response.blob();
        const dataUrl = URL.createObjectURL(blob);
        setQrDataUrl(dataUrl);
        
        // Track QR generation
        fetch(`${API_URL}/api/shares/track`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            platform: 'qr_code_generated',
            url: trackedUrl,
            source,
            title
          })
        }).catch(() => {});
      } catch (err) {
        console.error('QR generation failed:', err);
      } finally {
        setLoading(false);
      }
    };
    generateQR();
  }, [url, source, title]);

  const handleDownload = () => {
    if (!qrDataUrl) return;
    const link = document.createElement('a');
    link.href = qrDataUrl;
    link.download = `qr-code-${(title || 'share').replace(/\s+/g, '-').toLowerCase()}.png`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    toast.success('QR code downloaded!');
  };

  const handleCopy = async () => {
    if (!qrDataUrl) return;
    try {
      const response = await fetch(qrDataUrl);
      const blob = await response.blob();
      await navigator.clipboard.write([
        new ClipboardItem({ 'image/png': blob })
      ]);
      toast.success('QR code copied!');
    } catch (err) {
      await navigator.clipboard.writeText(getTrackedUrl());
      toast.success('Link copied!');
    }
  };

  return (
    <div className="flex flex-col items-center py-4">
      {loading ? (
        <div className="w-[200px] h-[200px] bg-muted animate-pulse rounded-lg flex items-center justify-center">
          <span className="text-muted-foreground">Generating...</span>
        </div>
      ) : qrDataUrl ? (
        <div className="bg-white p-4 rounded-lg shadow-sm">
          <img src={qrDataUrl} alt="QR Code" className="w-[200px] h-[200px]" data-testid="qr-code-image" />
        </div>
      ) : (
        <div className="w-[200px] h-[200px] bg-muted rounded-lg flex items-center justify-center">
          <span className="text-muted-foreground">Failed to load</span>
        </div>
      )}
      <p className="text-sm text-muted-foreground mt-4 text-center">{title || 'Scan to view'}</p>
      <p className="text-xs text-muted-foreground mt-1 max-w-[250px] truncate">{getTrackedUrl()}</p>
      <div className="flex gap-2 mt-4">
        <Button variant="outline" size="sm" onClick={handleCopy} disabled={!qrDataUrl}>
          <Copy className="w-4 h-4 mr-2" />
          Copy
        </Button>
        <Button size="sm" onClick={handleDownload} disabled={!qrDataUrl}>
          <Download className="w-4 h-4 mr-2" />
          Download
        </Button>
      </div>
    </div>
  );
};

// Quality badge component
const QualityBadge = ({ quality }) => {
  const config = {
    trusted: { icon: ShieldCheck, color: 'text-green-600', label: 'Trusted' },
    community: { icon: Shield, color: 'text-blue-500', label: 'Community' },
    unverified: { icon: ShieldAlert, color: 'text-gray-400', label: 'Unverified' },
    caution: { icon: AlertTriangle, color: 'text-amber-500', label: 'Caution' }
  };
  const { icon: Icon, color } = config[quality] || config.community;
  return <Icon className={`w-3 h-3 ${color}`} />;
};

// Platform configurations
const PLATFORMS = {
  twitter: {
    name: 'X (Twitter)',
    icon: Twitter,
    color: 'hover:bg-black hover:text-white',
    getShareUrl: (url, text) => 
      `https://twitter.com/intent/tweet?url=${encodeURIComponent(url)}&text=${encodeURIComponent(text)}`,
  },
  facebook: {
    name: 'Facebook',
    icon: Facebook,
    color: 'hover:bg-[#1877F2] hover:text-white',
    getShareUrl: (url, text) => 
      `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}&quote=${encodeURIComponent(text)}`,
  },
  whatsapp: {
    name: 'WhatsApp',
    icon: ({ className }) => (
      <svg className={className} viewBox="0 0 24 24" fill="currentColor">
        <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
      </svg>
    ),
    color: 'hover:bg-[#25D366] hover:text-white',
    getShareUrl: (url, text) => 
      `https://wa.me/?text=${encodeURIComponent(text + '\n\n' + url)}`,
  },
  pinterest: {
    name: 'Pinterest',
    icon: ({ className }) => (
      <svg className={className} viewBox="0 0 24 24" fill="currentColor">
        <path d="M12 0C5.373 0 0 5.372 0 12c0 5.084 3.163 9.426 7.627 11.174-.105-.949-.2-2.405.042-3.441.218-.937 1.407-5.965 1.407-5.965s-.359-.719-.359-1.782c0-1.668.967-2.914 2.171-2.914 1.023 0 1.518.769 1.518 1.69 0 1.029-.655 2.568-.994 3.995-.283 1.194.599 2.169 1.777 2.169 2.133 0 3.772-2.249 3.772-5.495 0-2.873-2.064-4.882-5.012-4.882-3.414 0-5.418 2.561-5.418 5.207 0 1.031.397 2.138.893 2.738.098.119.112.224.083.345l-.333 1.36c-.053.22-.174.267-.402.161-1.499-.698-2.436-2.889-2.436-4.649 0-3.785 2.75-7.262 7.929-7.262 4.163 0 7.398 2.967 7.398 6.931 0 4.136-2.607 7.464-6.227 7.464-1.216 0-2.359-.631-2.75-1.378l-.748 2.853c-.271 1.043-1.002 2.35-1.492 3.146C9.57 23.812 10.763 24 12 24c6.627 0 12-5.373 12-12 0-6.628-5.373-12-12-12z"/>
      </svg>
    ),
    color: 'hover:bg-[#E60023] hover:text-white',
    getShareUrl: (url, text, title, imageUrl) => {
      let pinUrl = `https://pinterest.com/pin/create/button/?url=${encodeURIComponent(url)}&description=${encodeURIComponent(text)}`;
      if (imageUrl) pinUrl += `&media=${encodeURIComponent(imageUrl)}`;
      return pinUrl;
    },
  },
  linkedin: {
    name: 'LinkedIn',
    icon: Linkedin,
    color: 'hover:bg-[#0A66C2] hover:text-white',
    getShareUrl: (url, text) => 
      `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`,
  },
  telegram: {
    name: 'Telegram',
    icon: ({ className }) => (
      <svg className={className} viewBox="0 0 24 24" fill="currentColor">
        <path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z"/>
      </svg>
    ),
    color: 'hover:bg-[#0088cc] hover:text-white',
    getShareUrl: (url, text) => 
      `https://t.me/share/url?url=${encodeURIComponent(url)}&text=${encodeURIComponent(text)}`,
  },
  email: {
    name: 'Email',
    icon: Mail,
    color: 'hover:bg-gray-600 hover:text-white',
    getShareUrl: (url, text, title) => 
      `mailto:?subject=${encodeURIComponent(title || 'Check this out')}&body=${encodeURIComponent(text + '\n\n' + url)}`,
  },
};

/**
 * ShareButtons - Social sharing with tracking
 * 
 * Usage:
 * <ShareButtons
 *   url="https://example.com/post/123"
 *   title="My awesome post"
 *   description="Check out this cute dog!"
 *   source="post_detail"
 *   onRepost={(comment) => handleRepost(comment)}
 *   showRepost={true}
 * />
 */
const ShareButtons = ({
  url,
  title = '',
  description = '',
  source = 'share',
  imageUrl = null,
  petType = null, // Optional: filter subreddit suggestions by pet type
  onRepost = null,
  showRepost = false,
  showCustomUrl = false,
  compact = false,
  className = ''
}) => {
  const [showRepostDialog, setShowRepostDialog] = useState(false);
  const [showCustomUrlDialog, setShowCustomUrlDialog] = useState(false);
  const [showRedditDialog, setShowRedditDialog] = useState(false);
  const [showQRDialog, setShowQRDialog] = useState(false);
  const [repostComment, setRepostComment] = useState('');
  const [customUrl, setCustomUrl] = useState('');
  const [redditSubreddit, setRedditSubreddit] = useState('');
  const [redditTitle, setRedditTitle] = useState('');
  const [subredditSearch, setSubredditSearch] = useState('');
  const [sharing, setSharing] = useState(false);
  
  // Subreddit state
  const [subreddits, setSubreddits] = useState([]);
  const [subredditSort, setSubredditSort] = useState('popularity'); // popularity, alphabetical
  const [loadingSubreddits, setLoadingSubreddits] = useState(false);

  // Fetch subreddits from API
  const fetchSubreddits = async (sortBy = 'popularity') => {
    setLoadingSubreddits(true);
    try {
      let url = `${API_URL}/api/subreddits?sort_by=${sortBy}&limit=20`;
      if (petType) url += `&pet_type=${petType}`;
      
      const response = await fetch(url);
      if (response.ok) {
        const data = await response.json();
        setSubreddits(data);
      }
    } catch (err) {
      console.log('Failed to fetch subreddits:', err);
    } finally {
      setLoadingSubreddits(false);
    }
  };

  // Fetch subreddits when dialog opens or sort changes
  useEffect(() => {
    if (showRedditDialog) {
      fetchSubreddits(subredditSort);
    }
  }, [showRedditDialog, subredditSort, petType]);

  // Filter subreddits by search
  const filteredSubreddits = subredditSearch
    ? subreddits.filter(s => 
        s.name.toLowerCase().includes(subredditSearch.toLowerCase()) ||
        (s.description && s.description.toLowerCase().includes(subredditSearch.toLowerCase()))
      )
    : subreddits;

  // Get the full URL with UTM tracking parameters
  const getFullUrl = (platform = null) => {
    let baseUrl = url;
    if (!baseUrl.startsWith('http')) {
      baseUrl = `${window.location.origin}${url}`;
    }
    
    // Add UTM parameters for tracking
    if (platform) {
      const utmParams = new URLSearchParams({
        utm_source: platform,
        utm_medium: 'social',
        utm_campaign: source || 'share'
      });
      const separator = baseUrl.includes('?') ? '&' : '?';
      return `${baseUrl}${separator}${utmParams.toString()}`;
    }
    
    return baseUrl;
  };

  // Get URL without tracking (for display/copy)
  const getBaseUrl = () => {
    if (url.startsWith('http')) return url;
    return `${window.location.origin}${url}`;
  };

  // Track share action
  const trackShare = async (platform) => {
    try {
      await fetch(`${API_URL}/api/shares/track`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          platform,
          url: getFullUrl(),
          source,
          title
        })
      });
    } catch (err) {
      // Silent fail - don't block sharing
      console.log('Share tracking failed:', err);
    }
  };

  // Handle social share
  const handleShare = async (platform) => {
    const trackedUrl = getFullUrl(platform); // URL with UTM params
    const shareText = description || title;
    
    // Track the share
    await trackShare(platform);
    
    // Get share URL (Pinterest needs image URL)
    const shareUrl = platform === 'pinterest' 
      ? PLATFORMS[platform].getShareUrl(trackedUrl, shareText, title, imageUrl)
      : PLATFORMS[platform].getShareUrl(trackedUrl, shareText, title);
    
    // Open share window
    if (platform === 'email') {
      window.location.href = shareUrl;
    } else {
      window.open(shareUrl, '_blank', 'width=600,height=500,noopener,noreferrer');
    }
  };

  // Copy link to clipboard (with tracking param)
  const handleCopyLink = async () => {
    try {
      const trackedUrl = getFullUrl('copy_link');
      await navigator.clipboard.writeText(trackedUrl);
      await trackShare('copy_link');
      toast.success('Link copied to clipboard!');
    } catch (err) {
      toast.error('Failed to copy link');
    }
  };

  // Handle native share (mobile)
  const handleNativeShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: title,
          text: description,
          url: getFullUrl()
        });
        await trackShare('native');
      } catch (err) {
        if (err.name !== 'AbortError') {
          toast.error('Failed to share');
        }
      }
    }
  };

  // Handle repost with comment
  const handleRepost = async () => {
    if (!onRepost) return;
    
    setSharing(true);
    try {
      await onRepost(repostComment, getFullUrl());
      await trackShare('repost');
      setShowRepostDialog(false);
      setRepostComment('');
      toast.success('Reposted successfully!');
    } catch (err) {
      toast.error('Failed to repost');
    } finally {
      setSharing(false);
    }
  };

  // Handle custom URL share
  const handleCustomUrlShare = async () => {
    if (!customUrl.trim()) {
      toast.error('Please enter a URL');
      return;
    }

    try {
      // Validate URL
      new URL(customUrl);
      
      await trackShare('custom_url');
      window.open(customUrl, '_blank', 'noopener,noreferrer');
      setShowCustomUrlDialog(false);
      setCustomUrl('');
    } catch (err) {
      toast.error('Please enter a valid URL');
    }
  };

  // Handle Reddit share
  const handleRedditShare = async () => {
    const subreddit = redditSubreddit.trim().replace(/^r\//, '');
    if (!subreddit) {
      toast.error('Please select or enter a subreddit');
      return;
    }

    const postTitle = redditTitle.trim() || title || 'Check this out!';
    const trackedUrl = getFullUrl('reddit'); // URL with UTM params

    // Track the share
    await trackShare('reddit');
    
    // Track subreddit usage for popularity
    try {
      await fetch(`${API_URL}/api/subreddits/track-share?subreddit=${encodeURIComponent(subreddit)}`, {
        method: 'POST'
      });
    } catch (err) {
      // Silent fail
    }

    // Reddit submit URL format
    const redditUrl = `https://www.reddit.com/r/${subreddit}/submit?url=${encodeURIComponent(trackedUrl)}&title=${encodeURIComponent(postTitle)}`;
    
    window.open(redditUrl, '_blank', 'noopener,noreferrer');
    setShowRedditDialog(false);
    setRedditSubreddit('');
    setRedditTitle('');
    toast.success('Opening Reddit... (login may be required)');
  };

  // Filter subreddits by search - now uses API data
  const displaySubreddits = subredditSearch
    ? filteredSubreddits
    : filteredSubreddits;

  // Compact mode - icons with more options dropdown
  if (compact) {
    return (
      <TooltipProvider>
        <div className={`flex items-center gap-1 ${className}`}>
          {/* Primary share buttons - Twitter, Facebook, WhatsApp */}
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8 rounded-full hover:bg-black hover:text-white"
                onClick={() => handleShare('twitter')}
                data-testid="share-twitter"
              >
                <Twitter className="w-4 h-4" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>X (Twitter)</TooltipContent>
          </Tooltip>
          
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8 rounded-full hover:bg-[#1877F2] hover:text-white"
                onClick={() => handleShare('facebook')}
                data-testid="share-facebook"
              >
                <Facebook className="w-4 h-4" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>Facebook</TooltipContent>
          </Tooltip>
          
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8 rounded-full hover:bg-[#25D366] hover:text-white"
                onClick={() => handleShare('whatsapp')}
                data-testid="share-whatsapp"
              >
                <PLATFORMS.whatsapp.icon className="w-4 h-4" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>WhatsApp</TooltipContent>
          </Tooltip>
          
          {/* Copy Link */}
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8 rounded-full hover:bg-muted"
                onClick={handleCopyLink}
                data-testid="share-copy"
              >
                <Copy className="w-4 h-4" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>Copy Link</TooltipContent>
          </Tooltip>

          {/* More options dropdown */}
          <DropdownMenu>
            <Tooltip>
              <TooltipTrigger asChild>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8 rounded-full hover:bg-muted"
                    data-testid="share-more"
                  >
                    <MoreHorizontal className="w-4 h-4" />
                  </Button>
                </DropdownMenuTrigger>
              </TooltipTrigger>
              <TooltipContent>More options</TooltipContent>
            </Tooltip>
            <DropdownMenuContent align="end" className="w-48">
              <DropdownMenuItem onClick={() => handleShare('pinterest')} className="cursor-pointer">
                <PLATFORMS.pinterest.icon className="w-4 h-4 mr-3" />
                Pinterest
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => handleShare('linkedin')} className="cursor-pointer">
                <Linkedin className="w-4 h-4 mr-3" />
                LinkedIn
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => handleShare('telegram')} className="cursor-pointer">
                <PLATFORMS.telegram.icon className="w-4 h-4 mr-3" />
                Telegram
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => handleShare('email')} className="cursor-pointer">
                <Mail className="w-4 h-4 mr-3" />
                Email
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem 
                onClick={() => {
                  setRedditTitle(title);
                  setShowRedditDialog(true);
                }}
                className="cursor-pointer text-[#FF4500]"
              >
                <RedditIcon className="w-4 h-4 mr-3" />
                Post to Reddit
              </DropdownMenuItem>
              <DropdownMenuItem 
                onClick={() => setShowQRDialog(true)}
                className="cursor-pointer"
              >
                <QrCode className="w-4 h-4 mr-3" />
                QR Code
              </DropdownMenuItem>
              {navigator.share && (
                <DropdownMenuItem onClick={handleNativeShare} className="cursor-pointer">
                  <Send className="w-4 h-4 mr-3" />
                  More Options...
                </DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>

          {showRepost && onRepost && (
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8 rounded-full hover:bg-green-100 hover:text-green-600"
                  onClick={() => setShowRepostDialog(true)}
                  data-testid="share-repost"
                >
                  <RefreshCw className="w-4 h-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>Repost</TooltipContent>
            </Tooltip>
          )}
        </div>

        {/* Repost Dialog */}
        <Dialog open={showRepostDialog} onOpenChange={setShowRepostDialog}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <RefreshCw className="w-5 h-5" />
                Repost with Comment
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <Textarea
                value={repostComment}
                onChange={(e) => setRepostComment(e.target.value)}
                placeholder="Add your thoughts... (optional)"
                rows={3}
              />
              <div className="p-3 bg-muted rounded-lg text-sm">
                <p className="font-medium truncate">{title}</p>
                {description && (
                  <p className="text-muted-foreground truncate">{description}</p>
                )}
              </div>
            </div>
            <DialogFooter>
              <DialogClose asChild>
                <Button variant="outline">Cancel</Button>
              </DialogClose>
              <Button onClick={handleRepost} disabled={sharing}>
                {sharing ? 'Posting...' : 'Repost'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Reddit Dialog */}
        <Dialog open={showRedditDialog} onOpenChange={setShowRedditDialog}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <RedditIcon className="w-5 h-5 text-[#FF4500]" />
                Post to Reddit
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div>
                <label className="text-sm font-medium mb-2 block">Post Title</label>
                <Input
                  value={redditTitle}
                  onChange={(e) => setRedditTitle(e.target.value)}
                  placeholder={title || 'Enter a title for your post'}
                  data-testid="reddit-title-input"
                />
              </div>
              
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="text-sm font-medium">Choose Subreddit</label>
                  <div className="flex gap-1">
                    <Button
                      variant={subredditSort === 'popularity' ? 'secondary' : 'ghost'}
                      size="sm"
                      className="h-7 px-2 text-xs"
                      onClick={() => setSubredditSort('popularity')}
                    >
                      <TrendingUp className="w-3 h-3 mr-1" />
                      Popular
                    </Button>
                    <Button
                      variant={subredditSort === 'alphabetical' ? 'secondary' : 'ghost'}
                      size="sm"
                      className="h-7 px-2 text-xs"
                      onClick={() => setSubredditSort('alphabetical')}
                    >
                      <SortAsc className="w-3 h-3 mr-1" />
                      A-Z
                    </Button>
                  </div>
                </div>
                
                <div className="relative mb-2">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    value={redditSubreddit}
                    onChange={(e) => setRedditSubreddit(e.target.value)}
                    placeholder="r/subreddit or search below"
                    className="pl-10"
                    data-testid="reddit-subreddit-input"
                  />
                </div>
                
                <div className="max-h-48 overflow-y-auto space-y-1 border rounded-md p-1">
                  {loadingSubreddits ? (
                    <div className="text-center py-4 text-muted-foreground text-sm">Loading...</div>
                  ) : filteredSubreddits.length === 0 ? (
                    <div className="text-center py-4 text-muted-foreground text-sm">
                      No subreddits found. Type a custom one above.
                    </div>
                  ) : (
                    filteredSubreddits.map((sub) => (
                      sub.status !== 'not_allowed' && (
                        <button
                          key={sub.id || sub.name}
                          type="button"
                          className={`w-full text-left px-3 py-2 rounded-md text-sm transition-colors flex items-center gap-2 ${
                            redditSubreddit === sub.name 
                              ? 'bg-[#FF4500] text-white' 
                              : 'hover:bg-muted'
                          }`}
                          onClick={() => setRedditSubreddit(sub.name)}
                        >
                          <QualityBadge quality={sub.quality} />
                          <div className="flex-1 min-w-0">
                            <span className="font-medium">{sub.name}</span>
                            {sub.share_count > 0 && (
                              <span className="text-xs ml-1 opacity-60">({sub.share_count})</span>
                            )}
                            {sub.description && (
                              <span className="text-xs ml-2 opacity-70 truncate block">{sub.description}</span>
                            )}
                          </div>
                        </button>
                      )
                    ))
                  )}
                </div>
              </div>
              
              <p className="text-xs text-muted-foreground">
                Note: Reddit will require you to log in to post.
              </p>
            </div>
            <DialogFooter>
              <DialogClose asChild>
                <Button variant="outline">Cancel</Button>
              </DialogClose>
              <Button 
                onClick={handleRedditShare} 
                className="bg-[#FF4500] hover:bg-[#FF4500]/90"
                data-testid="reddit-submit"
              >
                Open Reddit
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* QR Code Dialog - using QRCodeGenerator inline */}
        {showQRDialog && (
          <Dialog open={showQRDialog} onOpenChange={setShowQRDialog}>
            <DialogContent className="max-w-sm">
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <QrCode className="w-5 h-5" />
                  QR Code
                </DialogTitle>
              </DialogHeader>
              <QRCodeInline
                url={url}
                title={title || 'Scan to view'}
                source={source}
              />
            </DialogContent>
          </Dialog>
        )}
      </TooltipProvider>
    );
  }

  // Full mode with dropdown
  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" size="sm" className={className} data-testid="share-button">
            <Share2 className="w-4 h-4 mr-2" />
            Share
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-56">
          {/* Social platforms */}
          {Object.entries(PLATFORMS).map(([key, platform]) => (
            <DropdownMenuItem
              key={key}
              onClick={() => handleShare(key)}
              className="cursor-pointer"
            >
              <platform.icon className="w-4 h-4 mr-3" />
              {platform.name}
            </DropdownMenuItem>
          ))}
          
          <DropdownMenuSeparator />
          
          {/* Copy link */}
          <DropdownMenuItem onClick={handleCopyLink} className="cursor-pointer">
            <Copy className="w-4 h-4 mr-3" />
            Copy Link
          </DropdownMenuItem>
          
          {/* Reddit */}
          <DropdownMenuItem 
            onClick={() => {
              setRedditTitle(title);
              setShowRedditDialog(true);
            }}
            className="cursor-pointer text-[#FF4500]"
          >
            <RedditIcon className="w-4 h-4 mr-3" />
            Post to Reddit
          </DropdownMenuItem>
          
          {/* QR Code */}
          <DropdownMenuItem 
            onClick={() => setShowQRDialog(true)}
            className="cursor-pointer"
          >
            <QrCode className="w-4 h-4 mr-3" />
            QR Code
          </DropdownMenuItem>
          
          {/* Native share (if available) */}
          {navigator.share && (
            <DropdownMenuItem onClick={handleNativeShare} className="cursor-pointer">
              <Send className="w-4 h-4 mr-3" />
              More Options...
            </DropdownMenuItem>
          )}
          
          {/* Repost option */}
          {showRepost && onRepost && (
            <>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onClick={() => setShowRepostDialog(true)}
                className="cursor-pointer text-green-600"
              >
                <RefreshCw className="w-4 h-4 mr-3" />
                Repost with Comment
              </DropdownMenuItem>
            </>
          )}
          
          {/* Custom URL option */}
          {showCustomUrl && (
            <>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onClick={() => setShowCustomUrlDialog(true)}
                className="cursor-pointer"
              >
                <ExternalLink className="w-4 h-4 mr-3" />
                Share to Custom URL
              </DropdownMenuItem>
            </>
          )}
        </DropdownMenuContent>
      </DropdownMenu>

      {/* Repost Dialog */}
      <Dialog open={showRepostDialog} onOpenChange={setShowRepostDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <RefreshCw className="w-5 h-5" />
              Repost with Comment
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <Textarea
              value={repostComment}
              onChange={(e) => setRepostComment(e.target.value)}
              placeholder="Add your thoughts... (optional)"
              rows={3}
              data-testid="repost-comment-input"
            />
            <div className="p-3 bg-muted rounded-lg text-sm">
              <p className="font-medium truncate">{title}</p>
              {description && (
                <p className="text-muted-foreground truncate">{description}</p>
              )}
            </div>
          </div>
          <DialogFooter>
            <DialogClose asChild>
              <Button variant="outline">Cancel</Button>
            </DialogClose>
            <Button onClick={handleRepost} disabled={sharing} data-testid="repost-submit">
              {sharing ? 'Posting...' : 'Repost'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Custom URL Dialog */}
      <Dialog open={showCustomUrlDialog} onOpenChange={setShowCustomUrlDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ExternalLink className="w-5 h-5" />
              Share to Custom URL
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <label className="text-sm font-medium mb-2 block">
                Enter the URL where you want to share this content:
              </label>
              <Input
                value={customUrl}
                onChange={(e) => setCustomUrl(e.target.value)}
                placeholder="https://example.com/share"
                type="url"
                data-testid="custom-url-input"
              />
              <p className="text-xs text-muted-foreground mt-1">
                The link will be copied to your clipboard when the page opens
              </p>
            </div>
          </div>
          <DialogFooter>
            <DialogClose asChild>
              <Button variant="outline">Cancel</Button>
            </DialogClose>
            <Button onClick={handleCustomUrlShare} data-testid="custom-url-submit">
              Open & Share
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reddit Dialog */}
      <Dialog open={showRedditDialog} onOpenChange={setShowRedditDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <RedditIcon className="w-5 h-5 text-[#FF4500]" />
              Post to Reddit
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <label className="text-sm font-medium mb-2 block">Post Title</label>
              <Input
                value={redditTitle}
                onChange={(e) => setRedditTitle(e.target.value)}
                placeholder={title || 'Enter a title for your post'}
              />
            </div>
            
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="text-sm font-medium">Choose Subreddit</label>
                <div className="flex gap-1">
                  <Button
                    variant={subredditSort === 'popularity' ? 'secondary' : 'ghost'}
                    size="sm"
                    className="h-7 px-2 text-xs"
                    onClick={() => setSubredditSort('popularity')}
                  >
                    <TrendingUp className="w-3 h-3 mr-1" />
                    Popular
                  </Button>
                  <Button
                    variant={subredditSort === 'alphabetical' ? 'secondary' : 'ghost'}
                    size="sm"
                    className="h-7 px-2 text-xs"
                    onClick={() => setSubredditSort('alphabetical')}
                  >
                    <SortAsc className="w-3 h-3 mr-1" />
                    A-Z
                  </Button>
                </div>
              </div>
              
              <div className="relative mb-2">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  value={redditSubreddit}
                  onChange={(e) => setRedditSubreddit(e.target.value)}
                  placeholder="r/subreddit or search below"
                  className="pl-10"
                />
              </div>
              
              <div className="max-h-48 overflow-y-auto space-y-1 border rounded-md p-1">
                {loadingSubreddits ? (
                  <div className="text-center py-4 text-muted-foreground text-sm">Loading...</div>
                ) : filteredSubreddits.length === 0 ? (
                  <div className="text-center py-4 text-muted-foreground text-sm">
                    No subreddits found. Type a custom one above.
                  </div>
                ) : (
                  filteredSubreddits.map((sub) => (
                    sub.status !== 'not_allowed' && (
                      <button
                        key={sub.id || sub.name}
                        type="button"
                        className={`w-full text-left px-3 py-2 rounded-md text-sm transition-colors flex items-center gap-2 ${
                          redditSubreddit === sub.name 
                            ? 'bg-[#FF4500] text-white' 
                            : 'hover:bg-muted'
                        }`}
                        onClick={() => setRedditSubreddit(sub.name)}
                      >
                        <QualityBadge quality={sub.quality} />
                        <div className="flex-1 min-w-0">
                          <span className="font-medium">{sub.name}</span>
                          {sub.share_count > 0 && (
                            <span className="text-xs ml-1 opacity-60">({sub.share_count})</span>
                          )}
                          {sub.description && (
                            <span className="text-xs ml-2 opacity-70 truncate block">{sub.description}</span>
                          )}
                        </div>
                      </button>
                    )
                  ))
                )}
              </div>
            </div>
            
            <p className="text-xs text-muted-foreground">
              Note: Reddit will require you to log in to post.
            </p>
          </div>
          <DialogFooter>
            <DialogClose asChild>
              <Button variant="outline">Cancel</Button>
            </DialogClose>
            <Button 
              onClick={handleRedditShare} 
              className="bg-[#FF4500] hover:bg-[#FF4500]/90"
            >
              Open Reddit
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* QR Code Dialog for Full Mode */}
      {showQRDialog && (
        <Dialog open={showQRDialog} onOpenChange={setShowQRDialog}>
          <DialogContent className="max-w-sm">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <QrCode className="w-5 h-5" />
                QR Code
              </DialogTitle>
            </DialogHeader>
            <QRCodeInline
              url={url}
              title={title || 'Scan to view'}
              source={source}
            />
          </DialogContent>
        </Dialog>
      )}
    </>
  );
};

export default ShareButtons;
